import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client.js';
import { Loader, Alert, Stat, relativeTime } from '../../components/ui.jsx';

const ACTION_LABEL = {
  create: '➕ Created',
  update: '✏️ Updated',
  delete: '🗑️ Deleted',
  bulk_create: '📋 Bulk created',
  reset_password: '🔑 Reset password for',
};

export default function SuperHome() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.superOverview().then(setData).catch((err) => setError(err.message));
  }, []);

  if (error) return <Alert kind="bad">{error}</Alert>;
  if (!data) return <Loader label="Loading the control room…" />;

  const { counts, bySchool, recentActivity } = data;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>🛡️ Control room</h1>
          <p>Everything across every school. Create accounts, manage admins and keep an eye on usage.</p>
        </div>
        <div className="row gap-sm wrap">
          <Link to="/manage/users" className="btn">➕ Register a user</Link>
          <Link to="/manage/schools" className="btn btn-ghost">🏫 Schools</Link>
        </div>
      </div>

      <div className="stat-grid">
        <Stat icon="🏫" tone="grape" value={counts.schools} label="Schools" />
        <Stat icon="🎒" tone="sky"   value={counts.students} label="Students" />
        <Stat icon="👩‍🏫" tone="mint" value={counts.admins} label="School admins" />
        <Stat icon="🟢" tone="sun"   value={counts.activeThisWeek} label="Active this week" />
      </div>

      <div className="stat-grid mt-md">
        <Stat icon="✅" tone="mint"   value={counts.lessonsCompleted} label="Lessons completed" />
        <Stat icon="📝" tone="sky"    value={counts.quizAttempts} label="Quiz attempts" />
        <Stat icon="⏸" tone="coral"  value={counts.students - counts.activeStudents} label="Paused students" />
        <Stat icon="🛡️" tone="grape" value={counts.superadmins} label="Superadmins" />
      </div>

      <div className="panel-grid mt-lg">
        <section className="card-flat">
          <div className="row spread wrap gap-sm">
            <h2 style={{ margin: 0 }}>School-by-school</h2>
            <Link to="/admin" className="btn btn-sm btn-ghost">Open school reports →</Link>
          </div>

          <div className="table-wrap mt-md">
            <table className="data-table">
              <thead>
                <tr>
                  <th>School</th>
                  <th className="num">Students</th>
                  <th className="num">Admins</th>
                  <th className="num">Lessons done</th>
                  <th className="num">Active 7d</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {bySchool.length === 0 && (
                  <tr>
                    <td colSpan={6} className="faint tc">
                      No schools yet — add one from the Schools page.
                    </td>
                  </tr>
                )}
                {bySchool.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <strong>{s.name}</strong>
                      <span className="faint small" style={{ display: 'block' }}>
                        {s.code}{s.city ? ` · ${s.city}` : ''}
                      </span>
                    </td>
                    <td className="num">{s.students}</td>
                    <td className="num">{s.admins}</td>
                    <td className="num">{s.lessonsCompleted}</td>
                    <td className="num">{s.activeThisWeek}</td>
                    <td>
                      <span className={`pill ${s.isActive ? 'pill-good' : 'pill-bad'}`}>
                        {s.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card-flat">
          <div className="row spread wrap gap-sm">
            <h2 style={{ margin: 0 }}>Recent changes</h2>
            <Link to="/manage/audit" className="btn btn-sm btn-ghost">Full log →</Link>
          </div>

          {recentActivity.length === 0 ? (
            <p className="faint mt-md">Nothing has been changed yet.</p>
          ) : (
            <ul className="audit-list mt-md">
              {recentActivity.map((a, i) => (
                <li key={i}>
                  <span className="audit-action">
                    {ACTION_LABEL[a.action] ?? a.action} <em>{a.entity_type}</em>
                    {a.details?.userId ? ` "${a.details.userId}"` : ''}
                    {a.details?.name ? ` "${a.details.name}"` : ''}
                  </span>
                  <span className="faint small">
                    {a.actor ?? 'system'} · {relativeTime(a.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}
